import * as yup from "yup";
import { SubscriptionTypes } from "constants/subscriptionTypes";
import { isDiscountInputAllowed } from "utils/subscriptionRulesUtils";

export const step1Schema = yup.object().shape({
  subscriptionType: yup
    .string()
    .oneOf(
      Object.values(SubscriptionTypes),
      "Please select a valid subscription type"
    )
    .required("Subscription type is required"),
});

export const step2Schema = yup.object().shape({
  moduleType: yup
    .string()
    .required("Module type is required")
    .min(1, "Please select a module type"),
});

export const step3Schema = yup.object().shape({
  duration: yup
    .string()
    .required("Duration is required")
    .min(1, "Please select a duration"),
  country: yup
    .string()
    .required("Country is required")
    .min(1, "Please select a country"),
});

export const step4Schema = yup.object().shape({
  // Pricing Strategy - Required for all subscription types
  pricingStrategy: yup.object().shape({
    strategy: yup
      .string()
      .required("Please select a pricing strategy")
      .oneOf(["global", "custom"], "Please select a valid pricing strategy"),
    moduleType: yup.string().notRequired(),
  }),

  // Global Pricing - Only if strategy is 'global' AND discount input is allowed
  globalPricing: yup
    .object()
    .when(["pricingStrategy.strategy", "$subscriptionType"], {
      is: (strategy, subscriptionType) =>
        strategy === "global" && isDiscountInputAllowed(subscriptionType),
      then: (schema) =>
        schema.shape({
          discountPercentage: yup
            .number()
            .typeError("Discount must be a number")
            .min(0, "Discount cannot be negative")
            .max(100, "Discount cannot exceed 100%")
            .required(
              "Global discount is required for global pricing strategy"
            ),
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
    
  customPricing: yup.object().when(['pricingStrategy.strategy', '$subscriptionType'], {
    is: (strategy, subscriptionType) => 
      strategy === 'custom', // Always validate if strategy is custom
    then: (schema) => schema.shape({
      selectedEntityIds: yup
        .array()
        .min(1, 'Please select at least one entity for custom pricing')
        .required('Entity selection is required for custom pricing'),
      customDiscount: yup
        .number()
        .when('$subscriptionType', {
          // Only require discount if discount input is allowed
          is: (subscriptionType) => isDiscountInputAllowed(subscriptionType),
          then: (schema) => schema
            .typeError('Discount must be a number')
            .min(0, 'Discount cannot be negative')
            .max(100, 'Discount cannot exceed 100%')
            .required('Custom discount percentage is required'),
          otherwise: (schema) => schema.notRequired()
        })
    }),
    otherwise: (schema) => schema.notRequired()
  }),

  // Configuration object
  config: yup.object().shape({
    // Cooling-Off Period
    coolingOffPeriod: yup.object().shape({
      enabled: yup.boolean(),
      days: yup.number().when("enabled", {
        is: true,
        then: (schema) =>
          schema
            .min(1, "Cooling-off period must be at least 1 day")
            .required("Cooling-off days are required when enabled"),
        otherwise: (schema) => schema.notRequired(),
      }),
      refundPercentage: yup.number().when("enabled", {
        is: true,
        then: (schema) =>
          schema
            .min(0, "Refund percentage cannot be negative")
            .max(100, "Refund percentage cannot exceed 100%"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),

    // Renewal Settings - Not for ONE_TIME
    renewal: yup.object().when("$subscriptionType", {
      is: (type) => type !== SubscriptionTypes.ONE_TIME,
      then: (schema) =>
        schema.shape({
          autoRenew: yup.boolean(),
          gracePeriodDays: yup.number().when("autoRenew", {
            is: true,
            then: (schema) =>
              schema
                .min(0, "Grace period cannot be negative")
                .required(
                  "Grace period is required when auto-renew is enabled"
                ),
            otherwise: (schema) => schema.notRequired(),
          }),
          reminderDaysBefore: yup.number().when("autoRenew", {
            is: true,
            then: (schema) =>
              schema
                .min(0, "Reminder days cannot be negative")
                .required(
                  "Reminder days are required when auto-renew is enabled"
                ),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Cancellation Policy - For CANCELLATION_ALLOWED
    cancellation: yup.object().when("$subscriptionType", {
      is: SubscriptionTypes.SUBSCRIPTION_CANCELLATION_ALLOWED,
      then: (schema) =>
        schema.shape({
          allowed: yup.boolean(),
          allowedWithinDays: yup
            .number()
            .min(1, "Cancellation window must be at least 1 day")
            .required("Cancellation window is required"),
          refundPercentage: yup
            .number()
            .min(0, "Refund percentage cannot be negative")
            .max(100, "Refund percentage cannot exceed 100%"),
          adminApprovalRequired: yup.boolean(),
        }),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Billing Configuration
    billing: yup.object().shape({
      proRataRefund: yup.boolean(),
      billingCycles: yup
        .number()
        .min(0, "Billing cycles cannot be negative")
        .integer("Billing cycles must be a whole number"),
      trialPeriodDays: yup
        .number()
        .min(0, "Trial period cannot be negative")
        .integer("Trial period must be a whole number"),
    }),
  }),
});

export const completeFormSchema = yup.object().shape({
  ...step1Schema.fields,
  ...step2Schema.fields,
  ...step3Schema.fields,
  ...step4Schema.fields,
});

export const getStepSchema = (step) => {
  switch (step) {
    case 0:
      return step1Schema;
    case 1:
      return step2Schema;
    case 2:
      return step3Schema;
    case 3:
      return step4Schema;
    default:
      return step1Schema;
  }
};

// import * as yup from 'yup';
// import { SubscriptionTypes } from 'constants/subscriptionTypes';

// export const step1Schema = yup.object().shape({
//   subscriptionType: yup
//     .string()
//     .oneOf(
//       Object.values(SubscriptionTypes),
//       'Please select a valid subscription type'
//     )
//     .required('Subscription type is required')
// });

// export const step2Schema = yup.object().shape({
//   moduleType: yup
//     .string()
//     .required('Module type is required')
//     .min(1, 'Please select a module type')
// });

// export const step3Schema = yup.object().shape({
//   duration: yup
//     .string()
//     .required('Duration is required')
//     .min(1, 'Please select a duration'),
//   country: yup
//     .string()
//     .required('Country is required')
//     .min(1, 'Please select a country')
// });

// export const step4Schema = yup.object().shape({
//   // Pricing Strategy - Conditional based on subscription type
//   pricingStrategy: yup.object().shape({
//     strategy: yup
//       .string()
//       .when('$subscriptionType', {
//         is: (type) => type !== SubscriptionTypes.SUBSCRIPTION_CANCELLATION_ALLOWED,
//         then: (schema) => schema.required('Please select a pricing strategy'),
//         otherwise: (schema) => schema.notRequired()
//       }),
//     moduleType: yup.string().notRequired()
//   }),

//   // Global Pricing - Only if strategy is 'global'
//   globalPricing: yup.object().when('pricingStrategy.strategy', {
//     is: 'global',
//     then: (schema) => schema.shape({
//       discountPercentage: yup
//         .number()
//         .min(0, 'Discount cannot be negative')
//         .max(100, 'Discount cannot exceed 100%')
//         .required('Global discount is required for global pricing strategy')
//     }),
//     otherwise: (schema) => schema.notRequired()
//   }),

//   // Custom Pricing - Only if strategy is 'custom'
//   customPricing: yup.object().when('pricingStrategy.strategy', {
//     is: 'custom',
//     then: (schema) => schema.shape({
//       selectedEntityIds: yup
//         .array()
//         .min(1, 'Please select at least one entity for custom pricing')
//         .required('Entity selection is required for custom pricing'),
//       customDiscount: yup
//         .number()
//         .min(0, 'Discount cannot be negative')
//         .max(100, 'Discount cannot exceed 100%')
//         .required('Custom discount percentage is required')
//     }),
//     otherwise: (schema) => schema.notRequired()
//   }),

//   // Configuration object
//   config: yup.object().shape({
//     // Cooling-Off Period
//     coolingOffPeriod: yup.object().shape({
//       enabled: yup.boolean(),
//       days: yup.number().when('enabled', {
//         is: true,
//         then: (schema) => schema
//           .min(1, 'Cooling-off period must be at least 1 day')
//           .required('Cooling-off days are required when enabled'),
//         otherwise: (schema) => schema.notRequired()
//       }),
//       refundPercentage: yup.number().when('enabled', {
//         is: true,
//         then: (schema) => schema
//           .min(0, 'Refund percentage cannot be negative')
//           .max(100, 'Refund percentage cannot exceed 100%'),
//         otherwise: (schema) => schema.notRequired()
//       })
//     }),

//     // Renewal Settings - Not for ONE_TIME
//     renewal: yup.object().when('$subscriptionType', {
//       is: (type) => type !== SubscriptionTypes.ONE_TIME,
//       then: (schema) => schema.shape({
//         autoRenew: yup.boolean(),
//         gracePeriodDays: yup.number().when('autoRenew', {
//           is: true,
//           then: (schema) => schema
//             .min(0, 'Grace period cannot be negative')
//             .required('Grace period is required when auto-renew is enabled'),
//           otherwise: (schema) => schema.notRequired()
//         }),
//         reminderDaysBefore: yup.number().when('autoRenew', {
//           is: true,
//           then: (schema) => schema
//             .min(0, 'Reminder days cannot be negative')
//             .required('Reminder days are required when auto-renew is enabled'),
//           otherwise: (schema) => schema.notRequired()
//         })
//       }),
//       otherwise: (schema) => schema.notRequired()
//     }),

//     // Cancellation Policy - For CANCELLATION_ALLOWED
//     cancellation: yup.object().when('$subscriptionType', {
//       is: SubscriptionTypes.SUBSCRIPTION_CANCELLATION_ALLOWED,
//       then: (schema) => schema.shape({
//         allowed: yup.boolean(),
//         allowedWithinDays: yup.number()
//           .min(1, 'Cancellation window must be at least 1 day')
//           .required('Cancellation window is required'),
//         refundPercentage: yup.number()
//           .min(0, 'Refund percentage cannot be negative')
//           .max(100, 'Refund percentage cannot exceed 100%'),
//         adminApprovalRequired: yup.boolean()
//       }),
//       otherwise: (schema) => schema.notRequired()
//     }),

//     // Billing Configuration
//     billing: yup.object().shape({
//       proRataRefund: yup.boolean(),
//       billingCycles: yup.number()
//         .min(0, 'Billing cycles cannot be negative')
//         .integer('Billing cycles must be a whole number'),
//       trialPeriodDays: yup.number()
//         .min(0, 'Trial period cannot be negative')
//         .integer('Trial period must be a whole number')
//     })
//   })
// });

// export const completeFormSchema = yup.object().shape({
//   ...step1Schema.fields,
//   ...step2Schema.fields,
//   ...step3Schema.fields,
//   ...step4Schema.fields
// });

// export const getStepSchema = (step) => {
//   switch (step) {
//     case 0:
//       return step1Schema;
//     case 1:
//       return step2Schema;
//     case 2:
//       return step3Schema;
//     case 3:
//       return step4Schema;
//     default:
//       return step1Schema;
//   }
// };
